'use client';

import FileUploadIconSvg from '@/assets/svgs/file-upload.svg';
import FileIconSvg from '@/assets/svgs/file.svg';
import { ContactList } from '@/containers/ContactLists/types';
import classNames from 'classnames';

import { inter500 } from '@/config/fonts';
import IconContainer from '@/shared/components/IconContainer/IconContainer';
import { timeAgo } from '@/shared/utils/date';
import { pluralize } from '@/shared/utils/string';
import { ReactNode } from 'react';

interface ContactListItemViewProps {
  className?: string;
  data: ContactList;
  actions?: ReactNode;
  shouldTruncateLengthyText?: boolean;
  style?: React.CSSProperties;
}

const ContactListItemView = ({
  className,
  data,
  actions,
  shouldTruncateLengthyText = true,
  style,
}: ContactListItemViewProps) => {
  const isProcessing = data.list_state === 'PENDING';
  const uploadedDate = data.upload_date || data.uploaded_date;
  const dateFriendlyFormat = uploadedDate ? new Date(uploadedDate).toLocaleString() : '';
  return (
    <div
      className={classNames('flex items-center justify-between gap-3', className)}
      style={style}>
      <div className="flex gap-3">
        <IconContainer
          icon={
            isProcessing ? (
              <FileUploadIconSvg className="h-5 w-5" />
            ) : (
              <FileIconSvg className="h-5 w-5" />
            )
          }
          className={classNames({
            'bg-transparent opacity-50': isProcessing,
          })}
        />
        <div
          className={classNames('flex flex-col gap-0.5 text-left', {
            tooltip: data.list_name.length > 47 && shouldTruncateLengthyText,
          })}
          data-tip={data.list_name}>
          <span
            className={classNames('text-sm', inter500.className, {
              'w-[350px] overflow-hidden text-ellipsis whitespace-nowrap':
                shouldTruncateLengthyText,
              'opacity-50': isProcessing,
            })}>
            {data.list_name}
          </span>
          {isProcessing ? (
            <span className="text-gray-default text-[13px] opacity-50">Uploading...</span>
          ) : (
            <span className="text-gray-default text-[13px]">
              {uploadedDate && (
                <span className="tooltip" data-tip={dateFriendlyFormat}>
                  <span>{timeAgo(uploadedDate)}</span>
                </span>
              )}
              {uploadedDate && <span className="px-1 before:content-['\00B7']" />}
              {typeof data.leads_remaining === 'undefined' && (
                <span className="capitalize opacity-50">
                  {data.list_state?.toLowerCase()}
                </span>
              )}

              {typeof data.leads_remaining !== 'undefined' && (
                <span>
                  {data.leads_remaining} {pluralize('contact', data.leads_remaining)}{' '}
                  remaining
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      <div className="actions shrink-0">
        {isProcessing ? (
          <div className="tooltip flex w-20 py-5" data-tip="Uploading...">
            <progress
              className="progress progress-primary h-1 w-16"
              value={50}
              max="100"
            />
          </div>
        ) : (
          actions
        )}
      </div>
    </div>
  );
};

export default ContactListItemView;
